import createAxiosInstance from '../support/AxiosSupport';

/**
 * Модульный кеш пользовательских предпочтений (`GET /profile/settings/preferences`)
 * по образцу `refsCache.ts` (CTHS-110): точки вызова диалога «Одно пришло — одно ушло»
 * (создание вещи, AI-распознавание, покупка) не дёргают эндпоинт на каждый рендер —
 * повторные обращения в пределах TTL обслуживаются из памяти, параллельные запросы
 * дедуплицируются в один in-flight вызов.
 *
 * `invalidatePreferencesCache()` вызывается при сохранении предпочтений, чтобы
 * следующая проверка увидела свежую настройку.
 */

export interface CachedUserPreferences {
    oneInOneOutEnabled?: boolean | null;
}

const PREFERENCES_TTL_MS = 5 * 60 * 1000;

let cached: {data: CachedUserPreferences; expiresAt: number} | null = null;
let inflight: Promise<CachedUserPreferences> | null = null;

/** Предпочтения текущего пользователя из кеша (или одним сетевым запросом). */
export async function fetchPreferences(): Promise<CachedUserPreferences> {
    if (cached && cached.expiresAt > Date.now()) {
        return cached.data;
    }
    if (inflight) {
        return inflight;
    }
    inflight = (async () => {
        const api = await createAxiosInstance();
        const res = await api.get<CachedUserPreferences>('/profile/settings/preferences');
        cached = {data: res.data ?? {}, expiresAt: Date.now() + PREFERENCES_TTL_MS};
        return cached.data;
    })();
    try {
        return await inflight;
    } finally {
        inflight = null;
    }
}

/** Включён ли режим «Одно пришло — одно ушло»; ошибки сети трактуются как false. */
export async function isOneInOneOutEnabled(): Promise<boolean> {
    try {
        const prefs = await fetchPreferences();
        return prefs.oneInOneOutEnabled === true;
    } catch {
        return false;
    }
}

/** Сброс кеша — зовётся после сохранения предпочтений (и из тестов). */
export function invalidatePreferencesCache(): void {
    cached = null;
    inflight = null;
}
